import { getAuthToken } from '~shared/util/authUtil'
import { PinExchangeResult, TokenErrorBody, TokenResponseBody } from '../Types/Authorization'

const authUrl = import.meta.env.VITE_AUTH_URL as string
const clientId = import.meta.env.VITE_AUTH_STEPUP_CLIENT_ID as string

// Retry-After (RFC 7231) is either delta-seconds or an HTTP-date; Number() on a date yields NaN.
function parseRetryAfterSeconds(header: string | null): number | undefined {
  if (!header) return undefined

  if (/^\d+$/.test(header.trim())) {
    return Number(header)
  }

  const dateMs = Date.parse(header)
  if (Number.isNaN(dateMs)) return undefined

  const seconds = Math.round((dateMs - Date.now()) / 1000)
  return seconds >= 0 ? seconds : undefined
}

export async function exchangePin(pin: string, actionId: string): Promise<PinExchangeResult> {
  // The token endpoint is plain OAuth2, not JSON — form-encoding is what /connect/token expects.
  const params = new URLSearchParams({
    grant_type: 'kds_pin',
    client_id: clientId,
    pin,
    scope: `elevation:${actionId}`,
  })

  try {
    const response = await fetch(`${authUrl}/connect/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${getAuthToken() ?? ''}`,
      },
      body: params,
    })

    if (response.status === 429) {
      const retryAfterSeconds = parseRetryAfterSeconds(response.headers.get('Retry-After'))
      return { outcome: 'cooldown', retryAfterSeconds }
    }

    if (!response.ok) {
      const errorBody = (await response.json().catch(() => undefined)) as TokenErrorBody | undefined
      if (errorBody?.error === 'invalid_grant') return { outcome: 'invalidPin' }
      if (errorBody?.error === 'unauthorized_client') return { outcome: 'ineligibleOperator' }
      return { outcome: 'transportError' }
    }

    const data = (await response.json().catch(() => undefined)) as TokenResponseBody | undefined
    if (!data?.access_token || typeof data.expires_in !== 'number') {
      return { outcome: 'transportError' }
    }

    return { outcome: 'success', accessToken: data.access_token, expiresIn: data.expires_in }
  } catch {
    return { outcome: 'transportError' }
  }
}
